<script setup lang="ts">
import type { Offer } from '~/api/pricing-offers'
import { CtPill } from '@camelot/components'
import { storeToRefs } from 'pinia'
import { ref } from 'vue'
import VehicleCategoryInformation from '~/components/vehicle/vehicle-card/common/VehicleCategoryInformation.vue'
import VehicleImage from '~/components/vehicle/vehicle-card/common/VehicleImage.vue'
import VehicleOptionsGrid from '~/components/vehicle/vehicle-card/common/VehicleOptionsGrid.vue'
import { formattedOfferPrice, formattedOfferPricePerDay } from '~/composables/offer-utils'
import { useDataLayer } from '~/composables/tracking/datalayer'
import { translate, useTranslations } from '~/composables/translate/translations'
import { useScrollIntoView } from '~/composables/utils/useScrollIntoView'
import { useOfferStore } from '~/stores/offer'

const props = defineProps<{ buildSeries?: string }>()

const offerStore = useOfferStore()
const {
  possibleOffers,
  selectedOffer,
} = storeToRefs(offerStore)
const { pushPrebookingCarClickButtonEvent } = useDataLayer()

const isSelectedCategory = computed<boolean>(() => selectedOffer.value
  ? selectedOffer.value.buildSeries === props.buildSeries
  : false)
const { elementRef } = useScrollIntoView(() => isSelectedCategory.value)

const tooltipIsOpened = ref<boolean>(false)

const offer = computed<Offer | undefined>(() => possibleOffers.value
  .find(offer => offer.buildSeries === props.buildSeries))

const formattedPricePerDay = computed<string | undefined>(() => offer.value
  ? formattedOfferPricePerDay(offer.value, useTranslations().language.value)
  : undefined,
)

const formattedPrice = computed<string | undefined>(() => offer.value
  ? formattedOfferPrice(offer.value, useTranslations().language.value)
  : undefined,
)

const isOfferUnavailable = computed<boolean>(() => offer.value?.label?.toUpperCase() === 'SOLD_OUT' || !offer.value)

function getOpacity(): string {
  return isOfferUnavailable.value || !offer.value ? 'opacity-40' : ''
}

function selectOffer() {
  if (isOfferUnavailable.value) {
    selectedOffer.value = undefined
    return
  }

  if (!offer.value || tooltipIsOpened.value) {
    return
  }

  selectedOffer.value = offer.value

  // Track vehicle card click
  pushPrebookingCarClickButtonEvent()
}

function tooltipClicked(isTooltipOpened: boolean) {
  tooltipIsOpened.value = isTooltipOpened
}
</script>

<template>
  <div ref="elementRef" data-testid="vehicle-card">
    <section
      class="ct-card"
      :class="
        [
          offer || isOfferUnavailable ? 'available cursor-pointer' : 'unavailable',
          isSelectedCategory ? 'selected' : '',
        ]"
      @click="selectOffer()"
    >
      <VehicleImage
        class="mx-auto mb-24" :build-series="props.buildSeries" :class="getOpacity()"
        data-testid="vehicle-image"
      />

      <article class="flex flex-row justify-between mb-24">
        <VehicleCategoryInformation
          :build-series="props.buildSeries"
          :opacity="getOpacity()"
          data-testid="vehicle-category-information"
          @tooltip-clicked="tooltipClicked"
        />

        <CtPill v-if="isOfferUnavailable || !offer" variant="info" class="h-fit" data-testid="sold_out_label">
          {{ translate('car_unavailable_prebook') }}
        </CtPill>

        <ul
          v-else class="flex flex-col text-right text-black-100" :class="getOpacity()"
          data-testid="vehicle-price-info"
        >
          <li class="ct-title-4">
            {{ formattedPricePerDay }}/{{ translate('amount_per_day') }}
          </li>
          <li class="text-sm font-bold h-[1rem]">
            {{ formattedPrice }} {{ translate('prebook_total_description') }}
          </li>
          <li class="text-xs h-[1rem]">
            {{ offer?.description }}
          </li>
        </ul>
      </article>

      <VehicleOptionsGrid :build-series="props.buildSeries" :class="getOpacity()" data-testid="vehicle-options-grid" />
    </section>
  </div>
</template>

<style scoped lang="scss">

</style>
